import type { ParticipantBehaviour } from '@piccolohealth/pbs-common';
import { ChakraV3, Spin } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { createModal } from '../../../components/generic/Modal';
import { PiccoloError } from '../../../components/generic/PiccoloError';
import { useParticipantBehaviourQuery } from '../../../graphql/hooks/useParticipantQuery';
import { useAppContext } from '../../../hooks/useAppContext';
import { ParticipantBehaviourRecordingsTable } from './ParticipantBehaviourRecordingsTable';

interface Props {
  participantId: string;
  participantBehaviour: ParticipantBehaviour;
}

export const ParticipantBehaviourRecordingsModal = createModal<Props>((props) => {
  const { participantId, participantBehaviour, modal } = props;

  const { visible, hide, remove } = modal;

  const { organization } = useAppContext();

  const { isLoading, error, data } = useParticipantBehaviourQuery({
    organizationId: organization.id,
    participantId,
    participantBehaviourId: participantBehaviour.id,
  });

  const participantBehaviours = P.compact([
    data?.organization?.participant?.behaviour as ParticipantBehaviour | null,
  ]);

  const content = P.run(() => {
    if (isLoading) {
      return <Spin />;
    }

    if (error) {
      return <PiccoloError error={error} />;
    }

    return (
      <ParticipantBehaviourRecordingsTable
        participantId={participantId}
        participantBehaviours={participantBehaviours}
      />
    );
  });

  return (
    <ChakraV3.Dialog.Root
      open={visible}
      onOpenChange={hide}
      onExitComplete={remove}
      size='cover'
      scrollBehavior='inside'
    >
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.DialogTitleSubtitle
            title={`${participantBehaviour.name} recordings`}
            subtitle='View all data entries for this behaviour'
          />
          <ChakraV3.Dialog.Body>{content}</ChakraV3.Dialog.Body>
          <ChakraV3.Dialog.Footer>
            <ChakraV3.HStack>
              <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                Close
              </ChakraV3.Button>
            </ChakraV3.HStack>
          </ChakraV3.Dialog.Footer>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
